import { type AppRouteConfigView } from "shared/mappers/database/app-route/app-route-config/app-route-config";
import { type SessionView } from "shared/mappers/database/session/session";
import { isPublicRoute } from "hub/src/utils/routes/isPublicRoute";

type HasRouteAccessParameters = {
  session?: Partial<SessionView> | null;
  route?: Partial<AppRouteConfigView>;
};

export const hasRouteAccess = (params: HasRouteAccessParameters): boolean => {
  const { session, route } = params;

  if (!route || isPublicRoute(route)) {
    return true;
  }

  if (!session || !session.societyUserId) {
    return false;
  }

  // Users in Admin mode always have access
  if (session.adminMode) {
    return true;
  }

  // Check if any tag restrictions exist
  const hasTagRestrictions =
    route?.everyTag?.length !== 0 ||
    route?.someTag?.length !== 0 ||
    route?.noneTag?.length !== 0;

  // If no tag restrictions, allow access
  if (!hasTagRestrictions) {
    return true;
  }

  // Check user tags

  const hasEveryTagMatch =
    route?.everyTag?.length === 0
      ? true
      : route?.everyTag?.every(
          (tag) => session?.tags?.includes(tag.tagName), // If the user does not have all the tags in the everyTag array, access is denied
        );

  const hasSomeTagMatch =
    route?.someTag?.length === 0
      ? true
      : route?.someTag?.some(
          (tag) => session?.tags?.includes(tag.tagName), // If the user does not have at least one of the tags in the someTag array, access is denied
        );

  const hasNoneTagMatch =
    route?.noneTag?.length === 0
      ? false
      : route?.noneTag?.some(
          (tag) => session?.tags?.includes(tag.tagName), // If the user has at least one of the tags in the noneTag array, access is denied
        );

  const shouldAllowAccess =
    hasEveryTagMatch && hasSomeTagMatch && !hasNoneTagMatch;

  return !!shouldAllowAccess;
};
